import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Plus, Edit, Trash2, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Tasks = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]); // All users for tagging
  const [selectedBoard, setSelectedBoard] = useState('general');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    board: 'general',
    status: 'todo',
    priority: 'medium',
    assigned_to: '',
    tagged_users: []
  });
  const [editingId, setEditingId] = useState(null);

  const boards = [
    { id: 'general', label: 'Общи', color: '#3b82f6' },
    { id: 'management', label: 'Мениджмънт', color: '#8b5cf6' },
    { id: 'technical', label: 'Технически', color: '#10b981' },
    { id: 'marketing', label: 'Маркетинг', color: '#f59e0b' },
    { id: 'sales', label: 'Търговски', color: '#ef4444' },
    { id: 'documentation', label: 'Документални', color: '#06b6d4' },
    { id: 'operational', label: 'Оперативни', color: '#ec4899' }
  ];

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, []);

  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${API}/tasks`);
      setTasks(res.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/tasks/${editingId}`, formData);
        toast.success('Задачата бе актуализирана!');
      } else {
        await axios.post(`${API}/tasks`, formData);
        toast.success('Задачата бе добавена!');
      }
      fetchTasks();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Грешка при запазване');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Сигурни ли сте?')) {
      try {
        await axios.delete(`${API}/tasks/${id}`);
        toast.success('Задачата бе изтрита!');
        fetchTasks();
      } catch (error) {
        toast.error('Грешка при изтриване');
      }
    }
  };

  const handleEdit = (task) => {
    setFormData({
      ...task,
      tagged_users: task.tagged_users || []
    });
    setEditingId(task.id);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      board: 'general',
      status: 'todo',
      priority: 'medium',
      assigned_to: '',
      tagged_users: []
    });
    setEditingId(null);
  };

  const toggleTaggedUser = (userId) => {
    const updated = formData.tagged_users.includes(userId)
      ? formData.tagged_users.filter(id => id !== userId)
      : [...formData.tagged_users, userId];
    setFormData({...formData, tagged_users: updated});
  };

  // Drag & Drop handler
  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    // No destination or same position
    if (!destination || 
        (destination.droppableId === source.droppableId && 
         destination.index === source.index)) {
      return;
    }

    // Find the task
    const task = tasks.find(t => t.id === draggableId);
    if (!task) return;

    // Update task status based on destination
    const newStatus = destination.droppableId; // "todo", "in_progress", "done"
    const updatedTask = { ...task, status: newStatus };

    // Optimistic update
    setTasks(tasks.map(t => t.id === task.id ? updatedTask : t));

    // Save to backend
    try {
      await axios.put(`${API}/tasks/${task.id}`, updatedTask);
      toast.success('Задачата бе преместена!');
    } catch (error) {
      toast.error('Грешка при преместване');
      // Revert on error
      fetchTasks();
    }
  };

  // Filter tasks by board and status
  const filteredTasks = tasks.filter(task => task.board === selectedBoard);
  const todoTasks = filteredTasks.filter(task => task.status === 'todo');
  const inProgressTasks = filteredTasks.filter(task => task.status === 'in_progress');
  const doneTasks = filteredTasks.filter(task => task.status === 'done');

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Unknown';
  };

  return (
    <div className="page-container" data-testid="tasks-page">
      <div className="page-header">
        <h1 className="page-title">{t('tasks')}</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-task-btn">
              <Plus size={20} /> Добави задача
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingId ? 'Редактирай' : 'Нова'} задача</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="task-form" data-testid="task-form">
              <Input
                placeholder="Заглавие на задачата"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
              />
              <textarea
                className="form-textarea"
                placeholder="Описание (опционално)"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
              <Select value={formData.board} onValueChange={(val) => setFormData({...formData, board: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Изберете борд" />
                </SelectTrigger>
                <SelectContent>
                  {boards.map(board => (
                    <SelectItem key={board.id} value={board.id}>{board.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={formData.status} onValueChange={(val) => setFormData({...formData, status: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Статус" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todo">За работа</SelectItem>
                  <SelectItem value="in_progress">В процес</SelectItem>
                  <SelectItem value="done">Завършена</SelectItem>
                </SelectContent>
              </Select>
              <Select value={formData.priority} onValueChange={(val) => setFormData({...formData, priority: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Приоритет" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">Висок</SelectItem>
                  <SelectItem value="medium">Среден</SelectItem>
                  <SelectItem value="low">Нисък</SelectItem>
                </SelectContent>
              </Select>
              <Select value={formData.assigned_to || undefined} onValueChange={(val) => setFormData({...formData, assigned_to: val || ''})}>
                <SelectTrigger>
                  <SelectValue placeholder="Назначи на (опционално)" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Tagged Users Multi-Select */}
              <div className="form-field">
                <label className="field-label">Тагнати потребители (ще получат нотификации)</label>
                <div className="permissions-grid">
                  {users.map(user => (
                    <label key={user.id} className="permission-item">
                      <Checkbox
                        checked={formData.tagged_users.includes(user.id)}
                        onCheckedChange={() => toggleTaggedUser(user.id)}
                      />
                      <span>{user.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Отказ
                </Button>
                <Button type="submit" data-testid="save-task-btn">Запази</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Board Selector */}
      <div className="board-selector">
        {boards.map(board => (
          <button
            key={board.id}
            className={`board-tab ${selectedBoard === board.id ? 'active' : ''}`}
            style={{ borderBottom: selectedBoard === board.id ? `3px solid ${board.color}` : 'none' }}
            onClick={() => setSelectedBoard(board.id)}
          >
            {board.label}
          </button>
        ))}
      </div>

      {/* Kanban Board with Drag & Drop */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="kanban-board">
          {/* TODO Column */}
          <Droppable droppableId="todo">
            {(provided, snapshot) => (
              <div 
                className={`kanban-column ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                <h3 className="kanban-column-title">За работа ({todoTasks.length})</h3>
                {todoTasks.map((task, index) => (
                  <Draggable key={task.id} draggableId={task.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`task-card priority-${task.priority} ${snapshot.isDragging ? 'dragging' : ''}`}
                      >
                        <div className="task-card-header">
                          <h4>{task.title}</h4>
                          <div className="task-actions">
                            <button onClick={(e) => { e.stopPropagation(); handleEdit(task); }}>
                              <Edit size={16} />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleDelete(task.id); }}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                        {task.description && <p className="task-description">{task.description}</p>}
                        <div className="task-meta">
                          {task.assigned_to && (
                            <span className="task-assignee"><User size={14} /> {getUserName(task.assigned_to)}</span>
                          )}
                          {task.tagged_users && task.tagged_users.length > 0 && (
                            <span className="task-tagged">+{task.tagged_users.length} тагнати</span>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>

          {/* IN PROGRESS Column */}
          <Droppable droppableId="in_progress">
            {(provided, snapshot) => (
              <div 
                className={`kanban-column ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                <h3 className="kanban-column-title">В процес ({inProgressTasks.length})</h3>
                {inProgressTasks.map((task, index) => (
                  <Draggable key={task.id} draggableId={task.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`task-card priority-${task.priority} ${snapshot.isDragging ? 'dragging' : ''}`}
                      >
                        <div className="task-card-header">
                          <h4>{task.title}</h4>
                          <div className="task-actions">
                            <button onClick={(e) => { e.stopPropagation(); handleEdit(task); }}>
                              <Edit size={16} />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleDelete(task.id); }}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                        {task.description && <p className="task-description">{task.description}</p>}
                        <div className="task-meta">
                          {task.assigned_to && (
                            <span className="task-assignee"><User size={14} /> {getUserName(task.assigned_to)}</span>
                          )}
                          {task.tagged_users && task.tagged_users.length > 0 && (
                            <span className="task-tagged">+{task.tagged_users.length} тагнати</span>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>

          {/* DONE Column */}
          <Droppable droppableId="done">
            {(provided, snapshot) => (
              <div 
                className={`kanban-column ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                <h3 className="kanban-column-title">Завършени ({doneTasks.length})</h3>
                {doneTasks.map((task, index) => (
                  <Draggable key={task.id} draggableId={task.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`task-card priority-${task.priority} ${snapshot.isDragging ? 'dragging' : ''}`}
                      >
                        <div className="task-card-header">
                          <h4>{task.title}</h4>
                          <div className="task-actions">
                            <button onClick={(e) => { e.stopPropagation(); handleEdit(task); }}>
                              <Edit size={16} />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleDelete(task.id); }}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                        {task.description && <p className="task-description">{task.description}</p>}
                        <div className="task-meta">
                          {task.assigned_to && (
                            <span className="task-assignee"><User size={14} /> {getUserName(task.assigned_to)}</span>
                          )}
                          {task.tagged_users && task.tagged_users.length > 0 && (
                            <span className="task-tagged">+{task.tagged_users.length} тагнати</span>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
    </div>
  );
};

export default Tasks;
